import * as React from "react"
import { useRecoilValue, useRecoilState, useSetRecoilState } from "recoil"
import { Box } from "@chakra-ui/react"
import useWindowSize from "../../../../hooks/useWindowSize"
import { MobileMainMediaScreenProps } from "./mobile-main-media-screen.interface"
import { FullScreenMobileInterface } from "../full-screen-mobile-interface"
import { useBrowserType } from "../../../../hooks/useBrowserType"
import { findSideRatio } from "../../../../util/findSideRatio"
import { isBigPlayIconState } from "../../../../recoil/atoms/bigPlayIcon"
import { usePictureInPicture } from "../../../../hooks/usePictureInPicture"
import { playerPropsSelector } from "../../../../recoil/atoms/player"
import {
  overlaySelector,
  showBadgeOverlayAtom
} from "../../../../recoil/atoms/overlay"

import { productOverlaySelector } from "../../../../recoil/atoms/product-overlay"
import useLeaveOverlay from "../../../../hooks/useLeaveOverlay"
import { auctionParamsSelector } from "../../../../recoil/atoms/auction"
import layoutStates from "../../../../recoil/atoms/layout"
import { useWatchParty, useWatchPartyController } from "../../../../hooks/useWatchParty"
import { screenShareUserSelector } from "../../../../recoil/atoms/screenShareUser"
import { mainWindowUserSelector } from "../../../../recoil/atoms/mainWindowUser"
import { usersSelector } from "../../../../recoil/atoms/users"
import {
  hasPermission,
  isAllowedNow,
  isPictureInPictureActive,
  showUserAvatar,
  wasAllowedBefore
} from "../../../../util"
import { useLocalUserType } from "../../../../hooks/useLocalUserType"
import { useCustomToast, ToastConfigProps } from "../../../../hooks/useToast"
import notifyUserIfPermissionChanged from "../../notifyUserIfPermissionChanged"
import { waitForElm } from "../../../../util/waitForElm"
// import { LeaveOverlay } from "../../leave-overlay"
import { MobileVideoScreen } from "./mobile-video-screen"
import { MobileWebCameraMainScreen } from "./mobile-web-camera-main-screen"
import { watchPartyHideControlsSelector, watchPartyScreenSelector } from "../../../../recoil/atoms/watchPartyScreen"
import { urlBarNarrowed } from "../../../../util"
import { calculateHeightDependingElement } from "../../../../util/calculateHeightDependingElement"
import FloatingContainer from "../../floating-container"
import { useResizeObserver } from "../../../../hooks/useResizeObserver"
import {
  brandingSelector,
  showTabsAtBottomOnMobileSelector
} from "../../../../recoil/atoms/room"
import { LeaveOverlay } from "../../leave-overlay"
import { ElementId } from "../../../../constants/enums"
import { BigPlayButton } from "../BigPlayButton"
import {
  audienceTotalCountSelector,
  broadcasterTotalCountSelector,
  moderatorTotalCountSelector
} from "../../../../recoil/atoms/userCounts"
import AutoPlayFailedOverlay from "../../autoplay-failed-overlay"
import useShowFullScreenInterface from "../../../../conditionals/mobile-main-media-screen/useShowFullScreenInterface"
import { useCurrentAvatarInfo } from "src/conditionals/Avatar"
import usePlayerVisibility from "src/recoil/atoms/player/usePlayerVisibility"
import { ResizableBox } from "react-resizable"
import { MODAL_Z_INDEX } from "src/constants"
import { useVideoPopup } from "../../floating-container/useVideoPopup"
import { withErrorBoundary } from "react-error-boundary"
import ComponentFallback from "src/components/common/errors/componentFallback"

const UserType = window.MeetOnline?.UserType
const UserPermission = window.MeetOnline?.UserPermission

const _MobileMainMediaScreen: React.FC<MobileMainMediaScreenProps> = props => {
  const {
    videoConstraints,
    isTransparent,
    onResizeVideo,
    videoSize,
    localUserProperties,
    isOnlyScreensView,
    isMWDraggable
  } = props

  const { showFullScreenInterface } = useShowFullScreenInterface(props)
  const { isAvatar3D } = useCurrentAvatarInfo()

  const isLandscape = window.matchMedia("(orientation: landscape)").matches
  const { isModerator } = useLocalUserType()
  const floatingContainerRef = React.useRef(null)
  const [containerWidth, containerHeight] =
    useResizeObserver(floatingContainerRef)
  const [, setHideLeaveButton] = useRecoilState(layoutStates.hideLeaveButton)
  const overlay = useRecoilValue(overlaySelector)
  const { status, isShowOverlay } = useRecoilValue(auctionParamsSelector)
  const [isOverlay] = useRecoilState<boolean>(showBadgeOverlayAtom)
  const isFloatingDivActive = useRecoilValue(layoutStates.isFloatingDivActive)
  const setKeepLeaveOverlayActive = useSetRecoilState(
    layoutStates.keepLeaveOverlayActive
  )
  const { showAvatarPopupAndWPOnMVW } = useVideoPopup()

  const mainWindowUser = useRecoilValue(mainWindowUserSelector)
  const mainWindowUserDetails = useRecoilValue(
    usersSelector(mainWindowUser?.uid?.replace("ss", "").trim())
  )

  const [isBigPlayIcon, setBigPlayIcon] =
    useRecoilState<boolean>(isBigPlayIconState)

  const [targetTabIndex] = useRecoilState<number>(
    layoutStates.historyTabEnabledMobile
  )
  const hideControls = useRecoilValue(watchPartyHideControlsSelector)
  const [fullScreen] = useRecoilState(layoutStates.fullScreen)
  const { isWatchParty, watchPartyHostId, watchPartyType, isAudioWatchParty } = useWatchParty()

  const [showOnVideoOverlayContent, setShowOnVideoOverlayContent] =
    useRecoilState(layoutStates.showOnVideoOverlayContent)

  const { screenSize } = useWindowSize()

  const [showLeaveOverlay, setShowLeaveOverlay] = useRecoilState(layoutStates.showLeaveOverlay)
  const showTabsAtBottomOnMobile = useRecoilValue(
    showTabsAtBottomOnMobileSelector
  )
  
  // Show player for 10sec after joining
  const { togglePlayerVisibility } = usePlayerVisibility()
  React.useEffect(() => {
    togglePlayerVisibility({ autoHide: true, hideDelay: 10000 })
  }, [])

  /** This method will show player controller and cam/mic overlay */
  const showOverlay = value => {
    setShowLeaveOverlay(value)
    if (!value) {
      setPlayerProps({ isOpenControlsMobile: false })
    } else {
      setPlayerProps({ isOpenControlsMobile: true })
    }
  }

  const setPlayerProps = useSetRecoilState(playerPropsSelector)
  const {
    isOpenControlsMobile
    // isEnabledToggleControlDesktop
  } = useRecoilValue(playerPropsSelector)
  const { isOpen } = useRecoilValue(productOverlaySelector)
  // const [timeStamp, setTimeStamp] = React.useState({
  //   start: 0,
  //   end: 0
  // })

  const {
    // isChromeIOS,
    isSafariMobile
    // isAndroidMobile, isSamsungBrowser
  } = useBrowserType()

  const { leavePictureinPicture } = usePictureInPicture()

  let hideUserAvatar = !showUserAvatar({
    userId: mainWindowUser.uid,
    isWatchParty,
    isAudioWatchParty: watchPartyType === "audio",
    isCamEnabled: mainWindowUserDetails?.camEnabled
  })

  React.useEffect(() => {
    if (!isWatchParty && isPictureInPictureActive()) {
      leavePictureinPicture()
      setPlayerProps({ action: "reset" })
    }

    setBigPlayIcon(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWatchParty])

  const handleSize = React.useMemo(
    () => [screenSize.width, screenSize.height],
    [screenSize.height, screenSize.width]
  )

  const defaultHeightMainScreen = React.useMemo<number>(() => {
    return findSideRatio(screenSize.width, 9, 16)
  }, [screenSize.width])

  const isUrlBarNarrowed = React.useMemo(() => {
    return urlBarNarrowed() && isSafariMobile && !showTabsAtBottomOnMobile
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenSize.height])

  // const mobileFullScreenChatPosition = React.useMemo<string>(() => {
  //   if (
  //     watchPartyHostId === "modNotPresentImage" ||
  //     watchPartyHostId === "modNotPresentOverlay"
  //   ) {
  //     return isChatInputOverlay
  //       ? isUrlBarNarrowed
  //         ? "82.5px"
  //         : "65px"
  //       : "20px"
  //   }

  //   if (isLandscape && isWatchParty && isOpenControlsMobile) {
  //     return isChatInputOverlay
  //       ? hideControls
  //         ? "65px"
  //         : "110px"
  //       : hideControls
  //       ? "20px"
  //       : "65px"
  //   }

  //   if (isLandscape && isWatchParty && !isOpenControlsMobile) {
  //     return isChatInputOverlay ? "65px" : "20px"
  //   }

  //   if (isLandscape) {
  //     return isChatInputOverlay ? "70px" : "20px"
  //   }

  //   if (isWatchParty && !isOpenControlsMobile && isTransparent) {
  //     return isChatInputOverlay
  //       ? isUrlBarNarrowed
  //         ? "80.5px"
  //         : "63px"
  //       : "15px"
  //   }

  //   if (isWatchParty && isOpenControlsMobile && isTransparent) {
  //     return isChatInputOverlay && !hideControls
  //       ? isUrlBarNarrowed
  //         ? "127.5px"
  //         : "110px"
  //       : isChatInputOverlay && hideControls
  //       ? isUrlBarNarrowed
  //         ? "77.5px"
  //         : "60px"
  //       : !isChatInputOverlay
  //       ? "15px"
  //       : "60px"
  //   }

  //   return !isWatchParty
  //     ? isChatInputOverlay
  //       ? isUrlBarNarrowed
  //         ? "80.5px"
  //         : "63px"
  //       : "15px"
  //     : `${FOOTER_HEIGHT}px`
  // }, [
  //   watchPartyHostId,
  //   isLandscape,
  //   isWatchParty,
  //   isOpenControlsMobile,
  //   isTransparent,
  //   isChatInputOverlay,
  //   isUrlBarNarrowed,
  //   hideControls
  // ])

  // Check if current user has cam or mic permission
  const checkCamOrMicPermission = React.useCallback(permissions => {
    const hasCamPermission = isAllowedNow(permissions, UserPermission.canUseCam)
    const hasMicPermission = isAllowedNow(permissions, UserPermission.canUseMic)
    return hasMicPermission || hasCamPermission
  }, [])

  // const _stylingWatchParty: React.CSSProperties = React.useMemo(() => {
  //   if (isWatchParty) {
  //     return {
  //       width: isLandscape
  //         ? findSideRatio(screenSize.height, 16, 9) + "px"
  //         : videoSize.width + "px",
  //       height: isOnlyScreensView
  //         ? `${defaultHeightMainScreen}px`
  //         : isLandscape
  //         ? screenSize.height + "px"
  //         : videoSize.height + "px"
  //     }
  //   } else {
  //     return {
  //       width: "100%",
  //       height: "100%"
  //     }
  //   }
  // }, [
  //   defaultHeightMainScreen,
  //   isLandscape,
  //   isOnlyScreensView,
  //   isWatchParty,
  //   screenSize.height,
  //   videoSize.height,
  //   videoSize.width
  // ])

  // Show onVideoOverlayContent if user don't have cam and mic permission
  React.useEffect(() => {
    let timerId = null
    const DISPLAY_CONTENT_TIMEOUT = 1000
    const hasCamOrMicPermission = checkCamOrMicPermission(
      localUserProperties.permissions
    )

    if (!hasCamOrMicPermission) {
      timerId = setTimeout(() => {
        setShowOnVideoOverlayContent(true)
      }, DISPLAY_CONTENT_TIMEOUT)
    } else {
      timerId && clearTimeout(timerId)
    }
    return () => timerId && clearTimeout(timerId)
  }, [])

  const handleOnClick = React.useCallback(event => {
    event.stopPropagation()
  }, [])

  const isChatInput = React.useMemo(() => {
    return !(isWatchParty && isTransparent)
  }, [isWatchParty, isTransparent])

  const positionOverlay = React.useMemo(() => {
    // if (!isWatchParty && targetTabIndex === 2) {
    //   return isOpen ? "0px" : "10px"
    // }
    if (!isChatInput && !isOpenControlsMobile) {
      return isOpen ? "0px" : "10px"
    }
    if (isWatchParty && isLandscape) {
      return "-120px"
    }
    if (
      isWatchParty &&
      (watchPartyType === "audio" ||
        watchPartyHostId === "modNotPresentVideo") &&
      isOpenControlsMobile
    ) {
      return "60px"
    }
    if (isTransparent) {
      return showLeaveOverlay ? "66px" : "16px"
    }
    return isOpen ? "0px" : showLeaveOverlay ? "60px" : "10px"
  }, [
    isWatchParty,
    isChatInput,
    isOpenControlsMobile,
    isLandscape,
    watchPartyType,
    watchPartyHostId,
    isTransparent,
    isOpen,
    showLeaveOverlay
  ])

  // const heightLeaveOverlay = React.useMemo<string>(
  //   () =>
  //     !(!isModerator && hideControls) &&
  //     watchPartyHostId !== "modNotPresentImage" &&
  //     watchPartyHostId !== "modNotPresentOverlay" &&
  //     isOpenControlsMobile &&
  //     !isOpen
  //       ? "calc(100% - 55px)"
  //       : "100%",
  //   [hideControls, isModerator, isOpenControlsMobile, isOpen, watchPartyHostId]
  // )

  const minConstraints = React.useMemo(
    () => [388, defaultHeightMainScreen],
    [defaultHeightMainScreen]
  )

  const draggableOpts = React.useMemo(() => {
    return {
      allowAnyClick: true,
      enableUserSelectHack: true,
      disabled: isOpen|| isLandscape,
      // handle the dragging only on drag-to-resize-icon component
      handle: isAvatar3D ? "#drag-to-resize-icon" : "",
      cancel: isFloatingDivActive ? "#main-mvw-container" : ""

    }
  }, [isOpen, isFloatingDivActive, isLandscape])

  const videoResizeHandles = React.useMemo(
    () => (!fullScreen ? ["s"] : ["e"]),
    [fullScreen]
  )

  React.useEffect(() => {
    if (
      !isModerator &&
      ((isAllowedNow(
        localUserProperties.permissions,
        UserPermission.canUseMic
      ) &&
        !wasAllowedBefore(
          localUserProperties.oldPermissions,
          UserPermission.canUseMic
        )) ||
        (isAllowedNow(
          localUserProperties.permissions,
          UserPermission.canUseCam
        ) &&
          !wasAllowedBefore(
            localUserProperties.oldPermissions,
            UserPermission.canUseCam
          )))
    ) {
      setKeepLeaveOverlayActive(true)
      setHideLeaveButton(true)
      setShowLeaveOverlay(true)
    }

    // if user don't have cam and mic permission, hide the active leave overlay
    if (
      !hasPermission(
        localUserProperties.permissions,
        UserPermission.canUseMic
      ) &&
      !hasPermission(localUserProperties.permissions, UserPermission.canUseCam)
    ) {
      setKeepLeaveOverlayActive(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localUserProperties.permissions])

  const shareScreenUser = useRecoilValue(screenShareUserSelector)

  const broadcasterCount = useRecoilValue(broadcasterTotalCountSelector)
  const moderatorCount = useRecoilValue(moderatorTotalCountSelector)
  const audienceCount = useRecoilValue(audienceTotalCountSelector)
  const alone =
    (broadcasterCount == 0 && moderatorCount == 0 && audienceCount == 1) ||
    (moderatorCount == 0 && audienceCount == 0 && broadcasterCount == 1) ||
    (broadcasterCount == 0 && audienceCount == 0 && moderatorCount <= 1)

  const roomStyles = useRecoilValue(brandingSelector)

  let mainWindowUserUid: string = ""
  mainWindowUserUid = mainWindowUser?.uid

  if (shareScreenUser && shareScreenUser.uid) {
    // mainWindowUserUid = shareScreenUser.uid
  } else {
    if (mainWindowUserUid === "modNotPresentVideo") {
      mainWindowUserUid = ""
    }
    if (mainWindowUserUid !== "") {
      // if (localUserProperties?.type !== UserType?.AUDIENCE && allUserList.length > 1 && (userIdsSmallWindows.list).length == 1 && (userIdsSmallWindows.list).indexOf(_localUserId) > -1){
      if (
        localUserProperties?.type !== UserType?.AUDIENCE &&
        // allUserList.length > 1 &&
        mainWindowUserUid === localUserProperties?.id &&
        (localUserProperties?.camEnabled === true ||
          localUserProperties?.micEnabled === true)
      ) {
        // if only one publisher in room and all other are audience members
        /* enableNBAOverlay = true */
      } else {
      }
    } else {
      // mainWindowUserUid = activeSpeaker?.uid
    }
  }

  const config: ToastConfigProps = {
    toastOptions: {
      containerStyle: {
        minWidth: "unset",
        maxWidth: "100%",
        margin: "0.1rem"
      }
    },
    wrapperStyles: {
      padding: "0.3rem"
    },
    titleStyles: {
      fontSize: "0.75rem",
      fontWeight: 700
    },
    descriptionStyles: {
      fontSize: "0.75rem"
    },
    position: "top",
    styleCrossIcon: {
      top: "0px",
      right: "0px"
    }
  }

  const showToastMessage = useCustomToast(config)

  React.useEffect(() => {
    notifyUserIfPermissionChanged(
      localUserProperties.permissions,
      localUserProperties.oldPermissions,
      showToastMessage,
      true
    )
  }, [
    localUserProperties.permissions,
    localUserProperties.oldPermissions,
    showToastMessage
  ])

  const handleFade = React.useCallback(async () => {
    try {
      const badgeElement = (await waitForElm("fade-badge")) as HTMLDivElement
      const overlayElement = (await waitForElm(
        "leave-overlay-container"
      )) as HTMLDivElement

      const hasCamOrMicPermission = checkCamOrMicPermission(
        localUserProperties.permissions
      )

      // Show feature overlay, if no cam or mic permission and showOnVideoOverlayContent is true
      const showFeatureOverlay =
        !hasCamOrMicPermission && showOnVideoOverlayContent
      if (showFeatureOverlay) {
        badgeElement.classList.remove("fade")
      }

      // hide the badge if showOnVideoOverlayContent is off
      if (!showOnVideoOverlayContent) {
        badgeElement.classList.add("fade")
      }

      if (!showOnVideoOverlayContent || showLeaveOverlay) {
        // show cam, mic button overlay. if user has permission
        hasCamOrMicPermission && overlayElement.classList.add("fast-appear")
      } else {
        // show bade and hide cam, mic button overlay
        overlayElement.classList.remove("fast-appear")
        badgeElement.classList.remove("fade")
      }
    } catch (e) {
      console.error(e)
    }
  }, [
    localUserProperties.permissions,
    showLeaveOverlay,
    showOnVideoOverlayContent,
    checkCamOrMicPermission
  ])

  React.useEffect(() => {
    handleFade()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showLeaveOverlay,
    isBigPlayIcon,
    overlay,
    isTransparent,
    isWatchParty,
    status,
    isShowOverlay,
    isOverlay,
    targetTabIndex,
    isOpen,
    isLandscape,
    showOnVideoOverlayContent,
    isFloatingDivActive
  ])

  // Hide media player only for video watch party and popup avatar is not enabled
  const hideMediaPlayer = React.useMemo(() => {
    return isWatchParty && !isAudioWatchParty && !showAvatarPopupAndWPOnMVW
  }, [ isWatchParty, isAudioWatchParty, showAvatarPopupAndWPOnMVW])

  // const toggleShowLeaveOverlayTouchEnd = React.useCallback(
  //   e => {
  //     if (e.timeStamp - timeStamp.start < 150) {
  //       if (showLeaveOverlay) {
  //         setPlayerProps({ isOpenControlsMobile: false })
  //       }
  //       if (!showLeaveOverlay) {
  //         setPlayerProps({ isOpenControlsMobile: true })
  //       }
  //       setShowLeaveOverlay(!showLeaveOverlay)

  //       /* setPlayerProps({isOpenControlsMobile: !isOpenControlsMobile}) */
  //     }
  //     setTimeStamp({ start: 0, end: 0 })
  //   },
  //   [setPlayerProps, setShowLeaveOverlay, showLeaveOverlay, timeStamp.start]
  // )

  // const toggleShowLeaveOverlay = React.useCallback(
  //   e => {
  //     e.preventDefault()
  //     e.stopPropagation()

  //     if (showLeaveOverlay) {
  //       if (isEnabledToggleControlDesktop) {
  //         setPlayerProps({
  //           isOpenControlsMobile: false
  //         })
  //       } else {
  //         setPlayerProps({ isOpenControlsMobile: false })
  //       }
  //     }
  //     if (!showLeaveOverlay) {
  //       if (isEnabledToggleControlDesktop) {
  //         setPlayerProps({
  //           isOpenControlsMobile: true
  //         })
  //       } else {
  //         setPlayerProps({ isOpenControlsMobile: true })
  //       }
  //     }
  //     setShowLeaveOverlay(!showLeaveOverlay)

  //     /* setPlayerProps({isOpenControlsMobile: !isOpenControlsMobile}) */
  //   },
  //   [
  //     isEnabledToggleControlDesktop,
  //     setPlayerProps,
  //     setShowLeaveOverlay,
  //     showLeaveOverlay
  //   ]
  // )

  const changeSizeIconDependingHeightVideo = React.useCallback(
    (minHeightIcon: number, maxHeightIcon: number) => {
      return calculateHeightDependingElement(
        minHeightIcon,
        maxHeightIcon,
        findSideRatio(screenSize.width, 9, 16),
        screenSize.height,
        videoSize.height
      )
    },
    [screenSize.height, screenSize.width, videoSize.height]
  )

  // const handleTouchStart = React.useCallback(
  //   e => {
  //     setTimeStamp({ ...timeStamp, start: e.timeStamp })
  //   },
  //   [timeStamp]
  // )

  const productSelectorSizeOverlayProps = React.useMemo(() => {
    return {
      height: isLandscape
        ? `${screenSize.height}px`
        : `calc(${defaultHeightMainScreen}px - 0.5rem)`,
      width: isLandscape
        ? `${findSideRatio(screenSize.height, 16, 9)}px`
        : `calc(${videoSize.width}px - 0.8rem)`,
      isLargeMenu: +videoSize.width - 60 > 700 && !isLandscape,
      isSmallMenu: +videoSize.width - 60 < 450,
      isFullSizeOverlay: isOpen,
      isLandscape: isLandscape
    }
  }, [
    isLandscape,
    screenSize.height,
    defaultHeightMainScreen,
    videoSize.width,
    isOpen
  ])

  const resizeVideo = React.useCallback(
    (handle: string, dragging) => {

      const renderVideoPlayer = () => {
        return (
          <MobileVideoScreen
            showLeaveOverlay={showLeaveOverlay}
            videoSize={videoSize}
            setShowLeaveOverlay={setShowLeaveOverlay}
            isOnlyScreensView={isOnlyScreensView}
            isLandscape={isLandscape}
            localUserProperties={localUserProperties}
            isUrlBarNarrowed={isUrlBarNarrowed}
            changeSizeIconDependingHeightVideo={
              changeSizeIconDependingHeightVideo
            }
          />
        )
      }

      return (
        <Box
          width={
            isLandscape
              ? findSideRatio(screenSize.height, 16, 9) + "px"
              : videoSize.width + "px"
          }
          height={
            isOnlyScreensView
              ? `${defaultHeightMainScreen}px`
              : isLandscape
              ? screenSize.height + "px"
              : videoSize.height + "px"
          }
          position={isLandscape ? "absolute" : "unset"}
          left={isLandscape && "50%"}
          transform={isLandscape && "translate(-50%)"}
        >

          {/* Show the shared photo or video player here, if the popup avatar is enabled. */}
          {showAvatarPopupAndWPOnMVW && isWatchParty && (
            <Box
              pos={"absolute"}
              width={isLandscape ? "100%" : videoSize.width + "px"}
              height={isLandscape ? "100%" : videoSize.height + "px"}
            >
              {renderVideoPlayer()}
            </Box>
          )}

          <FloatingContainer
            isMobile={true}
            width="100%"
            height="100%"
            position={isLandscape ? "absolute" : "relative"}
            display="inline-block"
          >
            {!hideMediaPlayer && (
              <MobileWebCameraMainScreen
                positionOverlay={positionOverlay}
                localUserProperties={localUserProperties}
                hideUserAvatar={hideUserAvatar}
                videoSize={videoSize}
                isUrlBarNarrowed={isUrlBarNarrowed}
                productSelectorSizeOverlayProps={
                  productSelectorSizeOverlayProps
                }
                showLeaveOverlay={showLeaveOverlay}
                setShowLeaveOverlay={showOverlay}
                isOnlyScreensView={isOnlyScreensView}
                isLandscape={isLandscape}
                isTransparent={isTransparent}
                changeSizeIconDependingHeightVideo={
                  changeSizeIconDependingHeightVideo
                }
              />
            )}

            {!showAvatarPopupAndWPOnMVW && isWatchParty && renderVideoPlayer()}

          </FloatingContainer>
        </Box>
      )
    },
    [
      changeSizeIconDependingHeightVideo,
      defaultHeightMainScreen,
      hideMediaPlayer,
      hideUserAvatar,
      isFloatingDivActive,
      isLandscape,
      isOnlyScreensView,
      isTransparent,
      isUrlBarNarrowed,
      isWatchParty,
      localUserProperties,
      positionOverlay,
      productSelectorSizeOverlayProps,
      screenSize.height,
      screenSize.width,
      setShowLeaveOverlay,
      showLeaveOverlay,
      videoSize,
      showAvatarPopupAndWPOnMVW
    ]
  )

  const heightLeaveOverlay = React.useMemo<string>(
    () => (showLeaveOverlay ? "calc(100% - 55px)" : "100%"),
    [showLeaveOverlay]
  )

  const videoOverlayWidth = React.useMemo(
    () =>
      isLandscape
        ? findSideRatio(screenSize.height, 16, 9) + "px"
        : videoSize.width + "px",
    [isLandscape, videoSize.width, screenSize.height]
  )

  const [timeStamp, setTimeStamp] = React.useState({
    start: 0,
    end: 0
  })

  const handleTouchStart = e => {
    e.preventDefault()
    e.stopPropagation()
    setTimeStamp({ ...timeStamp, start: e.timeStamp })
  }

  const toggleShowLeaveOverlayTouchEnd = React.useCallback(
    e => {
      if (e.timeStamp - timeStamp.start < 150 && !isOpen) {
        // hide the player controller
        setPlayerProps({ isOpenControlsMobile: !showLeaveOverlay })
        // hide the leave overlay
        setShowLeaveOverlay(!showLeaveOverlay)
      }
      setTimeStamp({ start: 0, end: 0 })
    },
    [
      isOpen,
      setPlayerProps,
      setShowLeaveOverlay,
      showLeaveOverlay,
      timeStamp.start
    ]
  )

  /** Play watch party video */
  const playWatchPartyVideo = () => {
    const video = document.getElementById("watchpartyvideoplayer") as HTMLVideoElement
    
    if(!video) return 
    video?.play()
    setPlayerProps({
      paused: false
    })
  }

  // This function play live video
  const playLiveVideo = () => {

    // Extract video element and play the video
    const container = document.getElementById(
      `player-1337-${mainWindowUser?.uid}`
    ) as HTMLDivElement
    if(!container) return

    // Get Cam video element
    const video = container.getElementsByTagName("video")?.[0]
    if(!video) return

    video.play()
  }

  // This function play watch party video and live video if present
  const playVideo = (event) => {
    event.stopPropagation()
    setBigPlayIcon(false)

    playWatchPartyVideo()
    playLiveVideo()
  }
  
  return (
  <>
  
    <Box
      width="100vw"
      height={
        isOnlyScreensView
          ? `${defaultHeightMainScreen}px`
          : isLandscape
          ? "100vh"
          : videoSize.height
      }
      isMobile={true}
      productProps={{
        width: isLandscape
          ? findSideRatio(screenSize.height, 16, 9)
          : videoSize.width,
        isLandscape
      }}
      // position={!isLandscape ? "relative" : null}
    >
      <Box
        id={ElementId.ON_WHOLESCREEN_VIDEO_CONTAINER}
        width={
          isLandscape
            ? findSideRatio(screenSize.height, 16, 9) + "px"
            : videoSize.width + "px"
        }
        maxWidth={screenSize.width + "px"}
        position={"absolute"}
        //isTransparent ? "absolute" : "unset"}
        // zIndex={showFullScreenInterface ? "1000000000": "1"}
        top="0"
        height={videoSize.height}
        left={isLandscape && "50%"}
        transform={isLandscape && "translate(-50%)"}
        transition="opacity 0.5s"
        opacity={showFullScreenInterface ? "1" : "0"}
      >
        {/* {showFullScreenInterface && (
          <FullScreenMobileInterface
            maxWidthOverlay={
              isLandscape
                ? screenSize.width < findSideRatio(screenSize.height, 16, 9)
                  ? screenSize.width - 60
                  : findSideRatio(screenSize.height, 16, 9) * 0.7
                : videoSize.width - 60
            }
            isLandscape={isLandscape}
            isTransparent={isTransparent}
            setShowLeaveOverlay={isOpen ? e => {} : setShowLeaveOverlay}
            showLeaveOverlay={!isOpen && showLeaveOverlay}
          />
        )} */}
      </Box>

      {/* Display big play button - required in IOS device when fullscreen exit */}
      {!isFloatingDivActive && (
        <BigPlayButton
          width={videoOverlayWidth}
          height={"100%"}
          enabled={isBigPlayIcon}
          handlePlay={playVideo}
          offTouch={false}
        />
      )}
      
      <AutoPlayFailedOverlay
        isMobile={true}
        isLandscape={isLandscape}
        width={videoOverlayWidth}
        height={heightLeaveOverlay}
        showLeaveOverlay={showLeaveOverlay}
      />
      
      {/* <Box
        width={
          isLandscape
            ? findSideRatio(screenSize.height, 16, 9) + "px"
            : videoSize.width + "px"
        }
        maxWidth={isFloatingDivActive ? "100%" : screenSize.width + "px"}
        height={isLandscape && heightLeaveOverlay}
        position={isLandscape ? "absolute" : null}
        zIndex={showLeaveOverlay ? "10000000000" : -1}
        top="0px"
        left={isLandscape && !isFloatingDivActive && "50%"}
        transform={isLandscape && !isFloatingDivActive && "translate(-50%)"}
      >
        <LeaveOverlay
          isMobile={true}
          handleTouchStart={handleTouchStart}
          handleTouchEnd={toggleShowLeaveOverlayTouchEnd}
          handleClickVideo={() => {}} // Empty function
          styling={{ maxWidth: screenSize.width + "px" }}
          isTouchDelay={false}
          isCustomPlayer={true}
          widthLeaveOverlay={findSideRatio(screenSize.height, 16, 9) + "px"}
          isMainMobileScreen={true}
          heightLeaveOverlay={isLandscape ? "100%" : heightLeaveOverlay}
        />
      </Box> */}
      
      <Box ref={floatingContainerRef} width="100%" height="100%">
        <ResizableBox
          overflow="hidden"
          axis={isMWDraggable ? "y" : "none"}
          onResize={onResizeVideo}
          width={screenSize.width}
          height={
            isOnlyScreensView
              ? `${defaultHeightMainScreen}px`
              : isLandscape
              ? "100vh"
              : videoSize.height
          }
          minConstraints={minConstraints}
          maxConstraints={videoConstraints}
          handle={resizeVideo}
          handleSize={handleSize}
          resizeHandles={videoResizeHandles}
          preventdefaulttouchmoveevent="true"
          cursor={"ns-resize"}
          onClick={handleOnClick}
          draggableOpts={draggableOpts}
        ></ResizableBox>
      </Box>
    </Box>
  </>
  )
}

export const MobileMainMediaScreen = withErrorBoundary(
  React.memo(_MobileMainMediaScreen),
  { fallback: <ComponentFallback /> }
)
